import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { SHOWN } from '@constants/limits';
import { TOAST_MS } from '@constants/timing';
import { Icon } from './Icon';
import { useExitGhost } from './useExitGhost';

/** A button in the toast ("Lihat"): runs and closes the toast. */
export interface ToastAction { label: string; onClick: () => void }
interface Toast { id: number; text: string; error?: boolean; tone?: 'critical'; action?: ToastAction }
type Push = (text: string, error?: boolean, extra?: { action?: ToastAction; tone?: 'critical' }) => void;

const ToastCtx = createContext<Push>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback<Push>((text, error = false, extra) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs.slice(-(SHOWN.toasts - 1)), { id, text, error, ...extra }]);
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), extra?.tone === 'critical' ? TOAST_MS.critical : error ? TOAST_MS.error : TOAST_MS.info);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      {/* a polite live region; a critical toast interrupts (role="alert") */}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <ToastItem key={t.id} t={t} onDismiss={() => setItems((xs) => xs.filter((x) => x.id !== t.id))} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

/** One toast: slides up when it arrives, slides down and fades when it leaves (its time is up, or closed). */
function ToastItem({ t, onDismiss }: { t: Toast; onDismiss: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useExitGhost(ref, 'toast');
  return (
    <div ref={ref} className={`toast ${t.error ? 'error' : ''} ${t.tone ?? ''}`} role={t.tone === 'critical' ? 'alert' : undefined}>
      <Icon name={t.error || t.tone ? 'warn' : 'check'} size={18} />
      <span>{t.text}</span>
      {t.action && (
        <button
          type="button"
          className="toast-action"
          onClick={() => {
            t.action?.onClick();
            onDismiss();
          }}
        >
          {t.action.label}
        </button>
      )}
      <button type="button" aria-label="Tutup pesan" onClick={() => onDismiss()}>
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}
